// Web API client — mirrors mobile/src/api/client.js so the request
// shape (envelope, refresh flow, error class) matches and the server
// can treat both clients identically. Persistence uses localStorage
// instead of SecureStore (no native option on the web).
//
// SECURITY NOTE: localStorage is XSS-readable. The web app's CSP
// blocks inline JS and third-party scripts (see server/src/app.js),
// so the attack surface is limited to a bug in our own bundle. If
// you ever loosen the CSP to allow third-party scripts, move the
// refresh token to an httpOnly cookie before doing so.

// API base resolution. VITE_API_BASE_URL is the full API root URL
// including the `/api` path prefix — that's the literal URL fetch()
// hits. The bare-host form also works: a trailing /api is stripped
// and re-added so either shape produces the same final URL.
//   VITE_API_BASE_URL=https://api.outglow.live/api  → BASE = host/api  ✓
//   VITE_API_BASE_URL=https://api.outglow.live      → BASE = host/api  ✓
//   VITE_API_BASE_URL unset (dev / single-service)  → BASE = /api      ✓
// SERVER_BASE keeps the bare host so callers can build sibling-path
// URLs (legal/, uploads/) that live outside the API mount.
const RAW_BASE = (import.meta.env.VITE_API_BASE_URL || '').trim();
const SERVER_BASE = RAW_BASE.replace(/\/+$/, '').replace(/\/api$/, '');
const BASE = `${SERVER_BASE}/api`;

export const API_BASE_URL = BASE;
export const SERVER_BASE_URL = SERVER_BASE;

export const tokenStore = {
  get:   () => localStorage.getItem('og.token'),
  set:   (t) => localStorage.setItem('og.token', t),
  clear: () => localStorage.removeItem('og.token')
};
export const refreshTokenStore = {
  get:   () => localStorage.getItem('og.refresh'),
  set:   (t) => localStorage.setItem('og.refresh', t),
  clear: () => localStorage.removeItem('og.refresh')
};

const sessionListeners = new Set();
export const onSessionCleared = (fn) => { sessionListeners.add(fn); return () => sessionListeners.delete(fn); };
const emitSessionCleared = () => sessionListeners.forEach((fn) => { try { fn(); } catch {} });

const verificationListeners = new Set();
export const onEmailVerificationRequired = (fn) => { verificationListeners.add(fn); return () => verificationListeners.delete(fn); };
const emitVerificationRequired = (d) => verificationListeners.forEach((fn) => { try { fn(d); } catch {} });

export class ApiError extends Error {
  constructor(message, { code, status, details } = {}) {
    super(message);
    this.name = 'ApiError';
    this.code = code || null;
    this.status = status || 0;
    this.details = details || null;
  }
}

function buildError(payload, status) {
  const code = payload?.error?.code || null;
  const message = payload?.error?.message || payload?.message || 'Request failed';
  const details = payload?.error?.details || null;
  return new ApiError(message, { code, status, details });
}

export const authSessionStore = {
  set: ({ token, refreshToken }) => {
    if (token) tokenStore.set(token);
    if (refreshToken) refreshTokenStore.set(refreshToken);
  },
  clear: ({ silent = false } = {}) => {
    tokenStore.clear();
    refreshTokenStore.clear();
    if (!silent) emitSessionCleared();
  }
};

function sessionId() {
  let id = localStorage.getItem('og.session');
  if (!id) {
    id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem('og.session', id);
  }
  return id;
}

// Endpoints that must never trigger a refresh-and-retry: a 401 from
// these IS the answer, not an expired session.
const AUTH_NO_REFRESH = ['/auth/login', '/auth/register', '/auth/refresh',
  '/auth/password/forgot', '/auth/password/reset'];

let refreshPromise = null;
async function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refreshToken = refreshTokenStore.get();
      if (!refreshToken) return null;
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Client-Session-Id': sessionId() },
        body: JSON.stringify({ refreshToken })
      });
      let payload = {};
      try { payload = await res.json(); } catch {}
      if (!res.ok) { authSessionStore.clear(); return null; }
      authSessionStore.set(payload.data || {});
      return payload.data?.token || null;
    })().finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

export async function requestEnvelope(path, options = {}, retry = true) {
  const token = tokenStore.get();
  const isFormData = options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'X-Client-Session-Id': sessionId(),
    ...(options.headers || {})
  };
  const res = await fetch(`${BASE}${path}`, { ...options, headers });
  let payload = {};
  try { payload = await res.json(); } catch {}
  if (!res.ok) {
    const authCanRefresh = AUTH_NO_REFRESH.indexOf(path) === -1;
    if (res.status === 401 && retry && authCanRefresh) {
      const refreshed = await refreshAccessToken();
      if (refreshed) return requestEnvelope(path, options, false);
      payload = { message: 'Session expired' };
    }
    if (res.status === 401) authSessionStore.clear();
    if (res.status === 403 && payload?.error?.code === 'EMAIL_VERIFICATION_REQUIRED') {
      emitVerificationRequired(payload.error.details || {});
    }
    throw buildError(payload, res.status);
  }
  if (payload && payload.success === false) throw buildError(payload, res.status);
  return payload;
}

export async function request(path, options = {}) {
  const payload = await requestEnvelope(path, options);
  return payload.data;
}

export function jsonOptions(method, body) {
  return { method, body: JSON.stringify(body || {}) };
}

// Upload via fetch — multipart FormData. Returns response data and
// reports progress through the callback if the browser supports
// XHR upload progress events (fetch doesn't natively).
/**
 * Multipart upload with progress.
 *
 * Mirrors the refresh-on-401 behaviour of requestEnvelope and of the
 * mobile client. It previously did none of that: a single un-retried
 * XHR that read the access token once. With a 15-minute token TTL and a
 * 30-minute upload timeout, any upload begun on a tab that had been open
 * a while died outright while the UI still believed it was signed in.
 *
 * The timeout was worse: `xhr.timeout` was set but no `ontimeout`
 * handler existed, and per the XHR spec a timeout fires `timeout`, not
 * `error`. So when it did fire, neither resolve nor reject ran and the
 * promise stayed pending forever — the upload UI hung with no way out.
 */
export async function uploadFormData(path, form, onProgress, retry = true) {
  const send = (token) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE}${path}`);
    xhr.setRequestHeader('X-Client-Session-Id', sessionId());
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable || typeof onProgress !== 'function') return;
      onProgress({ loaded: e.loaded, total: e.total, ratio: e.total > 0 ? e.loaded / e.total : 0 });
    };
    xhr.onload = () => {
      let payload = {};
      try { payload = JSON.parse(xhr.responseText); } catch {}
      if (xhr.status >= 200 && xhr.status < 300) resolve(payload.data);
      else reject(buildError(payload, xhr.status));
    };
    xhr.onerror = () => reject(new ApiError('Upload failed. Check your connection.'));
    // ApiError defaults status to 0, so a transport failure can never be
    // mistaken for a 401 by the retry below.
    xhr.ontimeout = () => reject(new ApiError('Upload timed out. Try a smaller file or a stronger connection.'));
    xhr.timeout = 30 * 60 * 1000;
    xhr.send(form);
  });

  try {
    return await send(tokenStore.get());
  } catch (error) {
    // Refresh through the shared singleton, never a bare /auth/refresh:
    // the server rotates refresh tokens with reuse detection, so two
    // concurrent refreshes would revoke the whole session family.
    if (error.status === 401 && retry && AUTH_NO_REFRESH.indexOf(path) === -1) {
      const refreshed = await refreshAccessToken();
      if (refreshed) return uploadFormData(path, form, onProgress, false);
    }
    if (error.status === 401) authSessionStore.clear();
    if (error.status === 403 && error.code === 'EMAIL_VERIFICATION_REQUIRED') {
      emitVerificationRequired(error.details || {});
    }
    throw error;
  }
}
